/**
 * roleGate — single source of truth for role-based dashboard tile/section
 * visibility (Slice 2a, contract: docs/ROLE-MATRIX-v1.md).
 *
 * Pure functions over the logged-in user's role. The user object lives in
 * localStorage 'user' as JSON with a `role` field.
 *
 * Roles (ladder): CREW_MEMBER ⊂ READ_ONLY ⊂ FOREMAN ⊂ CONSTRUCTION_SUPERVISOR ⊂
 * CONSTRUCTION_MANAGER ⊂ PROJECT_MANAGER ⊂ ADMIN ⊂ SUPER_ADMIN (schema.prisma
 * UserRole enum). SAFETY_MANAGER = worker baseline + all safety rows
 * (function, not a rung). CONSTRUCTION_SUPERVISOR is treated as FOREMAN tier;
 * CONSTRUCTION_MANAGER as PROJECT_MANAGER tier (both interim, Phase 3 refine).
 * READ_ONLY is worker tier (parity with server isWorkerTier). SUPER_ADMIN is
 * platform-only (RBAC PRD v0.2: support_view only, never a tenant operator) —
 * it maps to ADMIN tier for client/server parity and should never reach a
 * tenant UI.
 * Unknown/missing roles resolve to the MOST RESTRICTIVE tier (CREW_MEMBER).
 */

export type Role =
  | 'CREW_MEMBER'
  | 'READ_ONLY'
  | 'FOREMAN'
  | 'CONSTRUCTION_SUPERVISOR'
  | 'CONSTRUCTION_MANAGER'
  | 'PROJECT_MANAGER'
  | 'SAFETY_MANAGER'
  | 'ADMIN'
  | 'SUPER_ADMIN';

export type RoleTier = 'CREW_MEMBER' | 'FOREMAN' | 'PROJECT_MANAGER' | 'SAFETY_MANAGER' | 'ADMIN';

/** Dashboard tile/section identifiers gated on the ROLE-MATRIX-v1 table. */
export type TileKey =
  | 'CREATE_JHA'
  | 'PPE_INSPECTION'
  | 'REPORT_INCIDENT'
  | 'NEW_AUDIT'
  | 'INVESTIGATIONS'
  | 'CONSTRUCTION_PLAN'
  | 'VIEW_JHAS'
  | 'CONSTRUCTION_PLANS'
  | 'SAFETY_AUDITS'
  | 'ASSET_TRACKER'
  | 'CERTIFICATIONS'
  | 'PROJECTS'
  | 'MY_JOBS'
  | 'WEEKLY_ACTIVITY'
  | 'TEAM_DASHBOARD'
  | 'DOT_INSPECTION'
  | 'NOTIFICATIONS'
  | 'ANALYTICS'
  | 'AUDIT_QUESTIONS'
  | 'DOCUMENT_LIBRARY'
  | 'BILLING'
  | 'COMING_SOON'
  // Tiles present on the dashboard but not yet rows in the matrix; preserved
  // at their current (ADMIN-only) visibility until the matrix covers them.
  | 'TEAM'
  | 'FLEET'
  | 'PERMISSIONS_MATRIX';

/**
 * Collapse a raw role string into its matrix tier. CONSTRUCTION_SUPERVISOR is
 * FOREMAN tier (interim note, ROLE-MATRIX-v1). Anything unknown/missing is the
 * most restrictive tier (CREW_MEMBER) — fail closed.
 */
export function effectiveTier(role: string | null | undefined): RoleTier {
  switch (role) {
    case 'ADMIN':
      return 'ADMIN';
    case 'SUPER_ADMIN':
      // Platform-only principal (RBAC PRD v0.2: support_view only, never a
      // tenant operator). Client/server parity: the server's
      // NON_WORKER_ROLES treats it company-wide, so it maps to ADMIN tier
      // here too — it should never enter a tenant UI.
      return 'ADMIN';
    case 'READ_ONLY':
      // Parity with server isWorkerTier: READ_ONLY is worker tier.
      return 'CREW_MEMBER';
    case 'SAFETY_MANAGER':
      return 'SAFETY_MANAGER';
    case 'PROJECT_MANAGER':
    case 'CONSTRUCTION_MANAGER':
      return 'PROJECT_MANAGER';
    case 'FOREMAN':
    case 'CONSTRUCTION_SUPERVISOR':
      return 'FOREMAN';
    default:
      // CREW_MEMBER and any unknown role → most restrictive.
      return 'CREW_MEMBER';
  }
}

const MANAGEMENT: RoleTier[] = ['PROJECT_MANAGER', 'SAFETY_MANAGER', 'ADMIN'];
const FOREMAN_PLUS: RoleTier[] = ['FOREMAN', 'PROJECT_MANAGER', 'SAFETY_MANAGER', 'ADMIN'];
const SAFETY_ONLY: RoleTier[] = ['SAFETY_MANAGER', 'ADMIN'];
const ALL_TIERS: RoleTier[] = ['CREW_MEMBER', 'FOREMAN', 'PROJECT_MANAGER', 'SAFETY_MANAGER', 'ADMIN'];

/**
 * Visibility per ROLE-MATRIX-v1 "Dashboard tile matrix" (● visible, — hidden).
 * Every tile MUST have an explicit tier list here — no fallthrough default.
 */
const TILE_TIERS: Record<TileKey, RoleTier[]> = {
  CREATE_JHA: ALL_TIERS,
  PPE_INSPECTION: ALL_TIERS,
  REPORT_INCIDENT: ALL_TIERS,
  NEW_AUDIT: MANAGEMENT,
  INVESTIGATIONS: MANAGEMENT,
  CONSTRUCTION_PLAN: ALL_TIERS, // R4 (WD-T4): crew = view-only (view tile CONSTRUCTION_PLANS); the plan-WRITE CTA is surfaced FOREMAN+ via canCreatePlans
  VIEW_JHAS: ALL_TIERS, // assigned-jobs scope is Phase 2b
  CONSTRUCTION_PLANS: ALL_TIERS, // view-only for crew is Phase 2b
  SAFETY_AUDITS: MANAGEMENT,
  ASSET_TRACKER: ALL_TIERS, // s/f scope is Phase 2b
  CERTIFICATIONS: ALL_TIERS, // own-certs-only is Phase 2b
  PROJECTS: ALL_TIERS, // assigned-only scope is server-enforced
  MY_JOBS: ALL_TIERS,
  // R6 (WD-T4 2026-10-04): the page is the SAFETY-MANAGER weekly report
  // (investigations hours, offices visited, warehouses audited, meetings led) —
  // gated to safety+admin per the WD slice default; the old "Track your tasks"
  // copy misstated the destination for crew.
  WEEKLY_ACTIVITY: SAFETY_ONLY,
  TEAM_DASHBOARD: FOREMAN_PLUS,
  // U4 (WD-T4 2026-10-04): assignment-driven — any ladder role may be a
  // designated driver, so the role layer is ALL_TIERS and the dashboard
  // additionally requires ≥1 active vehicle assignment (dot_vehicle_assignments).
  // Non-assigned users (incl. foremen) never see the tile → no dead end.
  DOT_INSPECTION: ALL_TIERS,
  NOTIFICATIONS: ALL_TIERS,
  ANALYTICS: MANAGEMENT,
  AUDIT_QUESTIONS: SAFETY_ONLY,
  DOCUMENT_LIBRARY: SAFETY_ONLY,
  BILLING: ['ADMIN'],
  COMING_SOON: ['PROJECT_MANAGER', 'ADMIN'],
  TEAM: ['ADMIN'],
  // RBAC PRD v0.2: PM/CM get full vehicle visibility + fleet/DOT inspections.
  FLEET: MANAGEMENT,
  PERMISSIONS_MATRIX: ['ADMIN'],
};

/** Read the role off the localStorage 'user' object (null-safe). */
export function getStoredRole(storage: Storage = localStorage): string | null {
  try {
    const raw = storage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.role === 'string' ? parsed.role : null;
  } catch {
    return null;
  }
}

/** Can the given role see this dashboard tile/section? Pure — unit-testable. */
export function canSeeTile(role: string | null | undefined, tile: TileKey): boolean {
  return TILE_TIERS[tile].includes(effectiveTier(role));
}

/** Every tile key visible to the given role (used by tests + future pages). */
export function visibleTiles(role: string | null | undefined): TileKey[] {
  return (Object.keys(TILE_TIERS) as TileKey[]).filter((tile) => canSeeTile(role, tile));
}

// ── Page-level rules (Phase 2b, ROLE-MATRIX-v1 'Page-level rules') ──────────
// Single source of truth: pages import these helpers instead of scattering
// role-string checks. All fail closed — unknown/missing roles are CREW tier.

/**
 * /construction-plans "Create New Plan" CTA + /construction-plans/create
 * route guard: crew is view-only (plans are created by foremen and up).
 */
export function canCreatePlans(role: string | null | undefined): boolean {
  return effectiveTier(role) !== 'CREW_MEMBER';
}

/** /assets "Add Asset" button — crew sees their own assigned gear, cannot add. */
export function canAddAssets(role: string | null | undefined): boolean {
  return effectiveTier(role) !== 'CREW_MEMBER';
}

/**
 * S2-assets (Brian 2026-09-29 model): custom asset type management — mirrors
 * the server's ASSET_TYPE_WRITE_ROLES on asset.routes.ts
 * (ADMIN, PROJECT_MANAGER, CONSTRUCTION_MANAGER, SAFETY_MANAGER).
 */
export function canManageAssetTypes(role: string | null | undefined): boolean {
  return MANAGEMENT.includes(effectiveTier(role));
}

/**
 * S2-assets: Markets (locations) management — mirrors MARKET_WRITE_ROLES on
 * market.routes.ts (same four roles as asset types).
 */
export function canManageMarkets(role: string | null | undefined): boolean {
  return MANAGEMENT.includes(effectiveTier(role));
}

/**
 * Certification management (Add Certification / per-row Delete / Crew Cert
 * Share). Mirrors the server's CERT_WRITER_ROLES gate on cert.routes.ts —
 * crew tier gets 403 on cert writes, so the UI must not offer them.
 */
export function canManageCertifications(role: string | null | undefined): boolean {
  return effectiveTier(role) !== 'CREW_MEMBER';
}

/** /certifications list scope: crew sees only their own certifications. */
export function seesAllCerts(role: string | null | undefined): boolean {
  return canManageCertifications(role);
}

/**
 * Read the stored user off localStorage 'user' (null-safe). Pages need the id
 * (client-side row filtering for crew-scoped lists) as well as the role.
 */
export function getStoredUser(storage: Storage = localStorage): { id: number | null; role: string | null } {
  try {
    const raw = storage.getItem('user');
    if (!raw) return { id: null, role: null };
    const parsed = JSON.parse(raw);
    return {
      id: typeof parsed?.id === 'number' ? parsed.id : null,
      role: typeof parsed?.role === 'string' ? parsed.role : null,
    };
  } catch {
    return { id: null, role: null };
  }
}
